import * as CubiqTS from "../../../../cubiq-model-multi-asset";
import { Injectable } from "@angular/core";
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { BFXAppNotificationType } from "../../../model/core/notifications/BFXAppNotificationType";
import { INotificationEvent } from "../../../model/core/notifications/INotificationEvent";
import { Subject } from "rxjs";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BFXNotificationEventType } from "../../../model/core/notifications/BFXNotificationEventType";
import { BFXAppNotificationEventType } from "../../../model/core/notifications/BFXAppNotificationEventType";
import { DealtPricingService } from "../../pricing/impl/DealtPricingService";
import { NotificationGeneralHelper } from "../../../model/core/notifications/helpers/NotificationGeneralHelper";
import { CcyLookupService } from "../../model/impl/CcyLookupService";
import { NotificationTransactionHelper } from "../../../model/core/notifications/helpers/NotificationTransactionHelper";
import { CcyPairLookupService } from "../../../lookup/model/impl/CcyPairLookupService";
import { CubiqWorker } from "../../../worker/CubiqWorker";
import { SystemConfigPromiseService } from "shared/auth-services";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { TranslateService } from "@ngx-translate/core";
import {
  CONNECTION_DOWN,
  CONNECTION_OUT_OF_SERVICE,
  CONNECTION_UNKNOWN,
  CONNECTION_UP,
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_FQDN_DISP_TXT,
  SYSMON_EVENT_GROUP_FQDN_DISP_TXT,
  SYSMON_EVENT_SOURCE,
  SYSMON_EVENT_STATUS,
  SYSMON_EVENT_TIMESTAMP,
} from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants";
import { OrderDataPrivatizationService } from "shared/cubiq-services/model/privatization/OrderDataPrivatizationService";
import { DealPrivatizationService } from "shared/cubiq-services/model/privatization/DealPrivatizationService";
import { INotificationService } from "shared/cubiq-services/lookup/notification/INotificationService";

@Injectable()
export class NotificationService implements INotificationService {
  private notifEventSubjectMap: Map<BFXNotificationEventType, Subject<INotificationEvent>> =
    new Map();
  private appNotifEventSubjectMap: Map<BFXAppNotificationEventType, Subject<INotificationEvent>> =
    new Map();
  private _skipQueueIdSet = new Set<string>();
  private _skipQueueIdFadeTime = 60000;
  private _queuedNotificationEventMap = new Map<
    string,
    {
      notifEvent: INotificationEvent;
      eventType: BFXNotificationEventType;
    }
  >();
  private _queuedAppNotificationEventMap = new Map<
    string,
    {
      notifEvent: INotificationEvent;
      eventType: BFXAppNotificationEventType;
    }
  >();
  protected notifTypes: Set<BFXNotificationType>;
  protected appNotifTypes: Set<BFXAppNotificationType>;
  protected modelAppNotificationEventSubject: Subject<INotificationEvent> =
    new Subject<INotificationEvent>();
  private generalHelper: NotificationGeneralHelper;
  private notificationTransactionHelper: NotificationTransactionHelper;
  private localeString: string = "DD-MMM-YY HH:mm:ss";
  private manualExecutionCallNameText: string = "";

  public notificationUpdateSubject: Subject<Set<BFXNotificationType>> = new Subject<
    Set<BFXNotificationType>
  >();

  constructor(
    private pricingService: DealtPricingService,
    private ccyPairLookup: CcyLookupService,
    private _ccyPairLookup: CcyPairLookupService,
    private worker: CubiqWorker,
    private sysConfigService: SystemConfigPromiseService,
    private sysConfigUtilService: BfxSysConfigUtilService,
    public translationService: TranslateService,
    private orderPrivatizationService: OrderDataPrivatizationService,
    private dealPrivatizationService: DealPrivatizationService,
  ) {
    this.generalHelper = new NotificationGeneralHelper(ccyPairLookup, sysConfigUtilService);
    this.notificationTransactionHelper = new NotificationTransactionHelper(_ccyPairLookup);
    Object.keys(BFXNotificationEventType).forEach((key) => {
      this.notifEventSubjectMap.set(
        BFXNotificationEventType[key],
        new Subject<INotificationEvent>(),
      );
    });
    Object.keys(BFXAppNotificationEventType).forEach((key) => {
      this.appNotifEventSubjectMap.set(
        BFXAppNotificationEventType[key],
        new Subject<INotificationEvent>(),
      );
    });
  }

  public getNotificationSubjectsMap(): Map<BFXNotificationEventType, Subject<INotificationEvent>> {
    return this.notifEventSubjectMap;
  }

  public getAppNotificationSubjectsMap(): Map<
    BFXAppNotificationEventType,
    Subject<INotificationEvent>
  > {
    return this.appNotifEventSubjectMap;
  }

  public getNotificationSubject(eventType: BFXNotificationEventType) {
    return this.notifEventSubjectMap.get(eventType);
  }

  public getAppNotificationSubject(eventType: BFXAppNotificationEventType) {
    return this.appNotifEventSubjectMap.get(eventType);
  }

  public getModelAppNotificationSubject(): Subject<INotificationEvent> {
    return this.modelAppNotificationEventSubject;
  }

  /**
   * Should a popup notification with specified type be created and dispatched? Type will default to limit-specific unless orderType is
   * specified and equal to ALGO or BMALGO
   * @param notifType
   * @param orderType
   */
  public shouldNotify(notifType: BFXNotificationType, orderType?: CubiqTS.OrderType): boolean {
    // Check aginst the notification setting specific to the order type
    const mappedNotifType = this.getOrderPopupType(notifType, orderType);
    return this.notifTypes && this.notifTypes.has(mappedNotifType);
  }

  /**
   * Should an application notification with specified type be created and dispatched? Type will default to limit-specific unless orderType is
   * specified and equal to ALGO or BMALGO
   * @param appNotifType
   * @param orderType
   */
  public shouldAppNotify(
    appNotifType: BFXAppNotificationType,
    orderType?: CubiqTS.OrderType,
  ): boolean {
    // Check aginst the notification setting specific to the order type
    const mappedNotifType = this.getOrderApplicationType(appNotifType, orderType);
    return this.appNotifTypes && this.appNotifTypes.has(mappedNotifType);
  }

  public setNotificationTypes(types: Iterable<BFXNotificationType>): void {
    if (types) {
      this.notifTypes = new Set(types);
    } else {
      this.notifTypes = null;
    }
    this.notificationUpdateSubject.next(this.notifTypes);
  }

  public addNotificationType(type: BFXNotificationType) {
    if (null == type || null == this.notifTypes || this.notifTypes.has(type)) return;

    let types: Array<BFXNotificationType> = [...this.notifTypes];
    types.push(type);

    this.setNotificationTypes(types);
  }

  public removeNotificationType(type: BFXNotificationType) {
    if (null == type || null == this.notifTypes || !this.notifTypes.has(type)) return;

    let types: Array<BFXNotificationType> = [];
    this.notifTypes.forEach((found) => {
      if (found != type) {
        types.push(found);
      }
    });

    this.setNotificationTypes(types);
  }

  public setAppNotificationTypes(types: Iterable<BFXAppNotificationType>): void {
    if (types) {
      this.appNotifTypes = new Set(types);
    } else {
      this.appNotifTypes = null;
    }
  }

  private queueNotificationEvent(
    notifEvent: INotificationEvent,
    eventType: BFXNotificationEventType,
    queueId: string,
  ): void {
    this._queuedNotificationEventMap.set(queueId, {
      notifEvent: notifEvent,
      eventType: eventType,
    });
  }

  public fireQueuedNotificationEvent(queueId: string) {
    if (this._queuedNotificationEventMap.has(queueId)) {
      const { notifEvent, eventType } = this._queuedNotificationEventMap.get(queueId);
      this._queuedNotificationEventMap.delete(queueId);
      this.fireNotificationEvent(notifEvent, eventType);
    } else {
      this.addSkipQueueId(queueId);
    }
  }

  public fireNotificationEvent(
    notifEvent: INotificationEvent,
    eventType: BFXNotificationEventType,
    queueId?: string,
  ): void {
    if (queueId != null && !this._skipQueueIdSet.has(queueId)) {
      this.queueNotificationEvent(notifEvent, eventType, queueId);
    } else if (notifEvent != null) {
      this.notifEventSubjectMap.get(eventType).next(notifEvent);
    }
  }

  private queueAppNotificationEvent(
    notifEvent: INotificationEvent,
    eventType: BFXAppNotificationEventType,
    queueId: string,
  ): void {
    this._queuedAppNotificationEventMap.set(queueId, {
      notifEvent: notifEvent,
      eventType: eventType,
    });
  }

  public fireQueuedAppNotificationEvent(queueId: string) {
    if (this._queuedAppNotificationEventMap.has(queueId)) {
      const { notifEvent, eventType } = this._queuedAppNotificationEventMap.get(queueId);
      this._queuedAppNotificationEventMap.delete(queueId);
      this.fireAppNotificationEvent(notifEvent, eventType);
    } else {
      this.addSkipQueueId(queueId);
    }
  }

  private addSkipQueueId(queueId: string) {
    this._skipQueueIdSet.add(queueId);
    setTimeout(() => {
      this._skipQueueIdSet.delete(queueId);
    }, this._skipQueueIdFadeTime);
  }

  public fireAppNotificationEvent(
    notifEvent: INotificationEvent,
    eventType: BFXAppNotificationEventType,
    queueId?: string,
  ): void {
    if (queueId != null && !this._skipQueueIdSet.has(queueId)) {
      this.queueAppNotificationEvent(notifEvent, eventType, queueId);
    } else if (notifEvent != null) {
      this.appNotifEventSubjectMap.get(eventType).next(notifEvent);
    }
  }

  public fireNotificationClearEvent(
    notifEvent: INotificationEvent,
    eventType: BFXNotificationEventType,
  ): void {
    if (notifEvent.ids != null) {
      this.notifEventSubjectMap.get(eventType).next(notifEvent);
    }
  }

  public dataToClearEvent(
    ids: Array<any>,
    clearAcceptanceOnly: boolean = false,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: BFXNotificationType.CLEAR_NOTIFICATION,
      ids: ids,
      clearAcceptanceOnly: clearAcceptanceOnly,
    };
    return metaData;
  }

  public orderDataToNotification(
    orderData: CubiqTS.OrderDataImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      orderNotifData: {
        orderId: orderData.orderId.toString(),
        orderRef: orderData.orderRef,
        requestedOrderType: orderData.requestedOrderType,
        requestedOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(orderData),
        dealtTrackingRate:
          orderData.product === CubiqTS.Product.FWD || orderData.product === CubiqTS.Product.NDF
            ? this.pricingService.getBlotterFwdTracking(orderData)
            : this.pricingService.getBlotterSpotTracking(orderData),
        orderDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        instrument: this.generalHelper.instrumentHelper(orderData),
        clientIntent: orderData.clientIntent === CubiqTS.OrderIntent.BUY ? "Buy" : "Sell",
        clientName: orderData.clientName,
        fixedCode: orderData.fixedCode,
        dealtTruncatedAmt: this.generalHelper.amountHelper(
          orderData.leavesQty,
          orderData.fixedCode,
        ),
        dealtTotalAmt: this.generalHelper.amountHelper(orderData.orderQty, orderData.fixedCode),
        hasTrackingSide: orderData.trackingSide != null,
        deskId: CubiqTS.Long.isLong(orderData.deskId) ? orderData.deskId.toString() : "-1",
        fullOrderDataObj: orderData,
      },
    };
    return metaData;
  }

  public unsolicitedOrderDataToNotification(
    orderData: CubiqTS.OrderDataImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      orderNotifData: {
        orderId: orderData.orderId.toString(),
        orderRef: orderData.orderRef,
        requestedOrderType: orderData.requestedOrderType,
        requestedOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(orderData),
        dealtTrackingRate:
          orderData.product === CubiqTS.Product.FWD || orderData.product === CubiqTS.Product.NDF
            ? this.pricingService.getBlotterFwdTracking(orderData)
            : this.pricingService.getBlotterSpotTracking(orderData),
        orderDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        instrument: this.generalHelper.instrumentHelper(orderData),
        clientIntent: orderData.clientIntent === CubiqTS.OrderIntent.BUY ? "Buy" : "Sell",
        clientName: orderData.clientName,
        fixedCode: orderData.fixedCode,
        dealtTruncatedAmt: this.generalHelper.amountHelper(
          orderData.leavesQty,
          orderData.fixedCode,
        ),
        dealtTotalAmt: this.generalHelper.amountHelper(orderData.orderQty, orderData.fixedCode),
        hasTrackingSide: orderData.trackingSide != null,
        deskId: CubiqTS.Long.isLong(orderData.deskId) ? orderData.deskId.toString() : "-1",
        fullOrderDataObj: orderData,
      },
    };
    return metaData;
  }

  public orderDataToUnsolCanNotification(
    orderData: CubiqTS.OrderDataImpl,
    type: BFXNotificationType,
    alternateGroupId: string,
    alternateGroupName: string,
    makerName: string,
    opsId: string,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,

      orderNotifData: {
        orderId: orderData.orderId.toString(),
        orderRef: orderData.orderRef,
        requestedOrderType: orderData.requestedOrderType,
        requestedOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(orderData),
        dealtTrackingRate:
          orderData.product === CubiqTS.Product.FWD || orderData.product === CubiqTS.Product.NDF
            ? this.pricingService.getBlotterFwdTracking(orderData)
            : this.pricingService.getBlotterSpotTracking(orderData),
        orderDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        instrument: this.generalHelper.instrumentHelper(orderData),
        clientIntent: orderData.clientIntent === CubiqTS.OrderIntent.BUY ? "Buy" : "Sell",
        clientName: orderData.clientName,
        fixedCode: orderData.fixedCode,
        dealtTruncatedAmt: this.generalHelper.amountHelper(
          orderData.leavesQty,
          orderData.fixedCode,
        ),
        dealtTotalAmt: this.generalHelper.amountHelper(orderData.orderQty, orderData.fixedCode),
        hasTrackingSide: orderData.trackingSide != null,
        deskId: CubiqTS.Long.isLong(orderData.deskId) ? orderData.deskId.toString() : "-1",
        fullOrderDataObj: orderData,
      },
      alternateGroupId: alternateGroupId,
      alternateGroupName: alternateGroupName,
      maker: makerName,
      opsId: opsId,
    };
    return metaData;
  }

  public orderDataToUnsolCanAltNotification(
    orderData: CubiqTS.OrderDataImpl,
    type: BFXNotificationType,
    alternateGroupId: string,
    alternateGroupName: string,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,

      orderNotifData: {
        orderId: orderData.orderId.toString(),
        orderRef: orderData.orderRef,
        requestedOrderType: orderData.requestedOrderType,
        requestedOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(orderData),
        dealtTrackingRate:
          orderData.product === CubiqTS.Product.FWD || orderData.product === CubiqTS.Product.NDF
            ? this.pricingService.getBlotterFwdTracking(orderData)
            : this.pricingService.getBlotterSpotTracking(orderData),
        orderDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        instrument: this.generalHelper.instrumentHelper(orderData),
        clientIntent: orderData.clientIntent === CubiqTS.OrderIntent.BUY ? "Buy" : "Sell",
        clientName: orderData.clientName,
        fixedCode: orderData.fixedCode,
        dealtTruncatedAmt: this.generalHelper.amountHelper(
          orderData.leavesQty,
          orderData.fixedCode,
        ),
        dealtTotalAmt: this.generalHelper.amountHelper(orderData.orderQty, orderData.fixedCode),
        hasTrackingSide: orderData.trackingSide != null,
        deskId: CubiqTS.Long.isLong(orderData.deskId) ? orderData.deskId.toString() : "-1",
        fullOrderDataObj: orderData,
      },
      alternateGroupId: alternateGroupId,
      alternateGroupName: alternateGroupName,
    };
    return metaData;
  }

  public tradeDataToNotification(
    tradeDeal: CubiqTS.DealImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    const formattedRate = this.formatRateValue(
      tradeDeal.allInClientRate,
      tradeDeal.baseCcyCd,
      tradeDeal.nonBaseCcyCd,
      tradeDeal.isInvert,
      tradeDeal.productType,
      tradeDeal.orderType,
    );
    const instrument = tradeDeal.isInvert
      ? tradeDeal.nonBaseCcyCd + "/" + tradeDeal.baseCcyCd
      : tradeDeal.baseCcyCd + "/" + tradeDeal.nonBaseCcyCd;

    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType != null ? subType : null,
      tradeNotifData: {
        id: tradeDeal.id.toString(),
        dealRef: tradeDeal.dealRef,
        requestOrderType: tradeDeal.requestOrderType,
        requestOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(tradeDeal),
        dealDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        dealtCcy: tradeDeal.dealtCcy,
        productId: tradeDeal.productId,
        isBuy: this.generalHelper.isBuyHelper(tradeDeal, "isBuy"),
        allInClientRate: formattedRate,
        clientName: tradeDeal.clientName,
        truncatedDealtAmount: this.generalHelper.amountHelper(tradeDeal.amt1, tradeDeal.dealtCcy),
        valueDate: this.generalHelper
          .dateToMomentHelper(tradeDeal, "valueDate")
          .format(this.localeString),

        accountCode: tradeDeal.accountCode,
        accountName: tradeDeal.accountName,
        clientBranchCode: tradeDeal.clientBranchCd,
        channelCd: tradeDeal.channelCd,
        clientTraderName: tradeDeal.clientTraderName,
        clientCode: tradeDeal.clientCd.toString(),
        entryUserId: tradeDeal.entryUserCode,
        fixingSourceCd: tradeDeal.fixingCode,
        stpHold: tradeDeal.holdStp,
        instrument: instrument,
        orderQty: tradeDeal.amt1,
        usdEquivQty: tradeDeal.usdEquivAmt,
        orderState: CubiqTS.OrderState.FILLED,
        requestedOrderType: tradeDeal.requestOrderType,
        product: tradeDeal.productId,
        salesGroupId: tradeDeal.salesGroupId.toString(),
        traderGroupNames: tradeDeal.traderGroupId.toString(),
        settleDate: this.generalHelper
          .dateToMomentHelper(tradeDeal, "valueDate")
          .format(this.localeString),
        // for debuging
        orderId: tradeDeal.orderId.toString(),
        orderRef: tradeDeal.orderRef,
        dealState: CubiqTS.OrderDealState[tradeDeal.dealStateId],
        completeTradeDeal: tradeDeal,
      },
    };
    return metaData;
  }

  public rateDataToNotification(
    rate: any,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      rateNotifData: {
        providerCode: rate["providerCode"] ? rate["providerCode"] : null,
        exceptionTypeId: this.generalHelper.opsInterventionExceptionTypeIdHelper(rate),
        productCode:
          rate.productCode !== null || rate.productCode !== undefined ? rate.productCode : null,
        publishDate:
          rate.productDate !== null || rate.productDate !== undefined
            ? CubiqTS.moment(rate.publishDate).format("HH:mm:ss")
            : null,
        baseCurrency:
          rate.baseCurrency !== null || rate.baseCurrency !== undefined ? rate.baseCurrency : null,
        nonBaseCurrency:
          rate.nonBaseCurrency !== null || rate.nonBaseCurrency !== undefined
            ? rate.nonBaseCurrency
            : null,
      },
    };
    return metaData;
  }

  public interventionDataToNotification(
    interventionEvent: CubiqTS.InterventionEventImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      interventionNotifData: {
        bodyContent: this.notificationTransactionHelper.adapt(interventionEvent),
        clientIntent: this.notificationTransactionHelper.getClientIntent(
          interventionEvent.orderData.clientIntent,
        ),
        rateRequest: this.notificationTransactionHelper.getRequestRate(interventionEvent),
        requestedOrderType: interventionEvent.orderData.requestedOrderType,
        requestedOrderTypeAsString: this.generalHelper.requestOrderTypeHelper(
          interventionEvent.orderData,
        ),
        currencyPair: interventionEvent.currencyPair,
        diDate: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
        orderId: this.getInterventionOrderId(interventionEvent),
        orderRef: this.getInterventionOrderRef(interventionEvent),
        clientName: interventionEvent.orderData.clientName,
      },
    };
    return metaData;
  }

  public interventionFixingRateRequirementAltToNotification(
    interventionEvent: CubiqTS.InterventionEventImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let message = interventionEvent.fixingRateRequirement.commentsTxt.errorMessages.map((err) =>
      JSON.parse(err as any),
    );

    let event: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      interventionFixingRateReqAltNotifData: {
        requirementId: interventionEvent.fixingRateRequirement.requirementId.toString(),
        bodyContent: message[0].args[2],
        target: message[0].args[0],
        time: message[0].args[1],
      },
    };

    return event;
  }

  public fwdRollDataToNotification(
    interventionEvent: CubiqTS.InterventionEventImpl,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let bodyContent: string = "Netted portfolio requires manual forward pricing";
    let nonBm: boolean = false;

    if (
      interventionEvent.fwdRollOrderData != null &&
      Array.isArray(interventionEvent.fwdRollOrderData.orders) &&
      interventionEvent.fwdRollOrderData.orders.length == 1
    ) {
      let order: CubiqTS.OrderDataImpl = interventionEvent.fwdRollOrderData.orders[0];
      if (order.orderType != CubiqTS.OrderType.BENCHMARK) {
        let orderId: string = order.orderId.toString();
        bodyContent = `Order ID ${orderId} requires manual forward roll`;
        nonBm = true;
      }
    }

    let event: INotificationEvent = {
      notifType: type,
      notifSubType: subType,
      fwdRollPriceNotifData: {
        bodyContent: bodyContent,
        portfolioId: interventionEvent?.fwdRollOrderData?.portfolioId,
        nonBm: nonBm,
      },
    };

    return event;
  }

  private getInterventionOrderId(interventionEvent: CubiqTS.InterventionEventImpl): string {
    return interventionEvent.orderData != null &&
      interventionEvent.orderData.orderId instanceof CubiqTS.Long
      ? interventionEvent.orderData.orderId.toString()
      : null;
  }

  private getInterventionOrderRef(interventionEvent: CubiqTS.InterventionEventImpl): string {
    return interventionEvent.orderData != null ? interventionEvent.orderData.orderRef : null;
  }

  public actionFailDataToNotification(
    failDt: any,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      actionFailedNotifData: {
        failData: failDt,
        date: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
      },
    };
    return metaData;
  }

  public simpleMessageToNotification(
    message: string,
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
    title: string = null,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      message: message,
      title: title,
    };
    return metaData;
  }

  public positionToNotification(): INotificationEvent {
    return {
      notifType: BFXNotificationType.POSITION,
      notifSubType: BFXNotificationSubType.POSITION,
    };
  }

  public orderEntryDataToNotification(
    resp: any,
    type: BFXNotificationType,
    subType: BFXNotificationSubType,
    ordId: string = null,
    orderRef: string,
    mess: string = null,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      orderEntryNotifData: {
        orderId: ordId,
        orderRef: orderRef,
        response: resp,
        message: mess,
        date: CubiqTS.moment(Date.now()).format("HH:mm:ss"),
      },
    };
    return metaData;
  }

  public exportErrorToNotification(
    exportId: string,
    message: string,
    time: Date,
    noDownload: boolean,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: BFXNotificationType.EXPORT_IMPORT,
      exportImportNotifData: {
        exportId: exportId,
        bodyContent: message,
        time: time,
        noDownload: noDownload,
      },
    };

    return metaData;
  }

  public inflightChangeResponseToNotification(
    response: CubiqTS.ToggleInflightStateResultItemImpl,
    isEnable: boolean,
    time: Date,
  ): INotificationEvent {
    let messages: any[] = response.errors.errorMessages;
    let errors: string[] = messages
      .map((message: any) => JSON.parse(message))
      .map((value: any) => this.translationService.instant(value.id, value.args));
    let error: string = errors.join(". ");

    let metaData: INotificationEvent = {
      notifType: BFXNotificationType.ALGO_INFLIGHT_FAILED,
      notifSubType: isEnable
        ? BFXNotificationSubType.ALGO_INFLIGHT_ENABLE_FAIL
        : BFXNotificationSubType.ALGO_INFLIGHT_DISABLE_FAIL,
      algoInflighFailedData: {
        orderId: response.orderId.toString(),
        orderRef: (response as any).orderRef,
        bodyContent: error,
        time: time,
      },
    };

    return metaData;
  }

  public detailFillUnlockDataToNotification(
    ordId: string,
    type: BFXNotificationType,
    subType: BFXNotificationSubType,
    lkUser: string,
    orderRef: string,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType !== null && subType !== undefined ? subType : null,
      detailFillUnlockNotifData: {
        orderId: ordId,
        orderRef: orderRef,
        lockUser: lkUser,
      },
    };
    return metaData;
  }

  public bmarkDataToNotification(
    fixingId: string,
    fixingData: any,
    fixingEventData: CubiqTS.FixingEventImpl,
    bmOrderData: any[],
    type: BFXNotificationType,
    subType?: BFXNotificationSubType,
  ): INotificationEvent {
    let metaData: INotificationEvent = {
      notifType: type,
      notifSubType: subType != null ? subType : null,
      bmarkFixingUpdateData: {
        fixingId: fixingId, // fixingId added to track notifications in both ACTIVE and RECENT. This id is missing the suffix ACTIVE/RECENT
        bmarkFixingData: fixingData,
        fixingState: fixingEventData.fixingState,
        orderDataInFixing: bmOrderData,
      },
    };
    return metaData;
  }

  public systemMonitorEventToNotification(
    //jmh
    systemMonitorEvent: CubiqTS.SystemMonitorEventImpl,
  ): INotificationEvent {
    let subType: BFXNotificationSubType = null;

    const status = systemMonitorEvent.dataMap.get(SYSMON_EVENT_STATUS);
    switch (status) {
      case CONNECTION_UP:
        subType = BFXNotificationSubType.CONNECTION_MONITOR_CONNECTED;
        break;
      case CONNECTION_DOWN:
        subType = BFXNotificationSubType.CONNECTION_MONITOR_DISCONNECTED;
        break;
      case CONNECTION_OUT_OF_SERVICE:
        subType = BFXNotificationSubType.CONNECTION_MONITOR_OUT_OF_SERVICE;
        break;
      case CONNECTION_UNKNOWN:
        subType = BFXNotificationSubType.CONNECTION_MONITOR_UNKNOWN;
        break;
      default:
        subType = BFXNotificationSubType.CONNECTION_MONITOR_UNKNOWN;
        break;
    }

    let time: Date = new Date();
    const timestamp: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_TIMESTAMP);

    if (timestamp != null) {
      try {
        time = CubiqTS.moment.utc(timestamp, "YYYY/MM/DD HH:mm:ss.SSS").toDate();
      } catch (e) {
        logger.out.warn(
          this.constructor.name +
            ": Failed to convert system monitor notification event timestamp " +
            timestamp,
        );
      }
    }

    let connectionGroup: string = systemMonitorEvent.dataMap.get(
      SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".1",
    );
    let connection: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN_DISP_TXT);
    let session: string = null;
    let node: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_SOURCE);
    let fqdn: string = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN);

    if (systemMonitorEvent.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".2") != null) {
      connection = systemMonitorEvent.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".2");
      session = systemMonitorEvent.dataMap.get(SYSMON_EVENT_FQDN_DISP_TXT);
    }

    if (connectionGroup == null && connection != null) {
      connectionGroup = connection;
      connection = null;
    }

    const body: string =
      connectionGroup +
      " - " +
      (connection != null ? connection + " - " : "") +
      (session != null ? session + " - " : "") +
      node;

    const metaData: INotificationEvent = {
      notifType: BFXNotificationType.CONNECTION_MONITOR,
      notifSubType: subType,
      connectionMonitorNotifData: {
        bodyContent: body,
        eventId: fqdn + "_" + node,
        fqdn: fqdn,
        time: time,
      },
    };

    return metaData;
  }

  /**
   * Create notification event for a given inflight algo check event. It creates an
   * order data notification if the algo inflight check event is recognized.
   *
   * @param event the algo check event that the notification needs to be raised for
   * @returns null if the event action type is not supported or if the method failed to
   * create order data notification, the notification event otherwise
   */
  public inflightAlertEventToNotification(
    event: CubiqTS.InflightCheckEventImpl,
  ): INotificationEvent {
    let notificationSubType: BFXNotificationSubType = null;

    switch (event.actionType) {
      case CubiqTS.InflightActionType.ALERT:
        notificationSubType = BFXNotificationSubType.ALGO_INFLIGHT_ALERT;
        break;
      case CubiqTS.InflightActionType.CANCEL:
        notificationSubType = BFXNotificationSubType.ALGO_INFLIGHT_CANCEL;
        break;
      case CubiqTS.InflightActionType.PAUSE:
        notificationSubType = BFXNotificationSubType.ALGO_INFLIGHT_PAUSE;
        break;
      default:
        logger.out.warn(
          `${this.constructor.name}: Unknown inflight alert action type ${event.actionType}. Notification will be dropped.`,
        );
        break;
    }

    let notificationEvent: INotificationEvent = null;

    if (notificationSubType != null) {
      try {
        notificationEvent = this.orderDataToNotification(
          event.orderData,
          BFXNotificationType.ALGO_INFLIGHT,
          notificationSubType,
        );
        notificationEvent.inflightCheckEventNotifData = {
          ruleName: event.ruleName,
          ruleType: event.ruleType,
        };
      } catch (e) {
        logger.out.error(
          `${this.constructor.name}: Failed to create order data notification from inflight algo event ${e}`,
        );
      }
    }

    return notificationEvent;
  }

  handleGenericError(
    error: Error | CubiqTS.Errors,
    message: string = null,
    title: string = "generic.error.title",
  ): void {
    if (error instanceof Error) {
      if (message == null) {
        message = error.message;
      }
    } else if (message == null && error != null && error.errorMessages.length > 0) {
      try {
        message = error.errorMessages
          .map((err) => JSON.parse(err as any))
          .map((err) => this.translationService.instant(err.id, err.args))
          .join(",\n");
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}: Failed to construct generic error message from message object. Reason: ${e}`,
        );
      }
    }

    if (error != null && message != null && title != null) {
      this.fireNotificationEvent(
        this.simpleMessageToNotification(
          message,
          BFXNotificationType.GENERIC_ERROR,
          BFXNotificationSubType.GENERIC_ERROR,
          this.translationService.instant(title),
        ),
        BFXNotificationEventType.MESSAGE,
      );
    }
  }

  logGenericErrorMessages(error: CubiqTS.Errors, title: string = "generic.error.title"): void {
    if (error != null && error.errorMessages.length > 0) {
      try {
        let logMessage = error.errorMessages
          .map((err) => JSON.parse(err as any))
          .map((err) => this.translationService.instant(err.id, err.args))
          .join(",\n");

        console.log(`${title}: ${logMessage}`);
      } catch (e) {
        logger.out.warn(
          `${this.constructor.name}: Failed to construct generic log message from message object. Reason: ${e}`,
        );
      }
    }
  }

  // return true if error dispatched
  handleErrors(
    response: any,
    notifType: BFXNotificationType,
    notifSubType: BFXNotificationSubType,
  ): boolean {
    let errorsDispatched: boolean = false;
    if (response != null) {
      if (response.results != null && response.results.length > 0) {
        for (let result of response.results) {
          if (
            result.errors != null &&
            result.errors.errorMessages != null &&
            result.errors.errorMessages.length > 0
          ) {
            this.fireNotificationEvent(
              this.actionFailDataToNotification(result, notifType, notifSubType),
              BFXNotificationEventType.ACTION_FAIL_EVENT,
            );
            // this.fireModelNotificationFailureEvent(result, {notifType, notifSubType});
            errorsDispatched = true;
          }
        }
      } else if (response.errorMessages != null && response.errorMessages.length > 0) {
        let error: any = JSON.parse(response.errorMessages[0]);
        if (response["orderIds"] || response["dealIds"]) {
          let actionFailedIds: Array<string> =
            response["orderIds"] != undefined ? response["orderIds"] : response["dealIds"];
          actionFailedIds.forEach((value) => {
            response.orderId = value;
            this.fireNotificationEvent(
              this.actionFailDataToNotification(response, notifType, notifSubType),
              BFXNotificationEventType.ACTION_FAIL_EVENT,
            );
            // this.fireModelNotificationFailureEvent(response, {notifType,notifSubType});
            errorsDispatched = true;
          });
        }
      }
    }
    return errorsDispatched;
  }

  public getApplicationNotifications(
    descriptors: Array<CubiqTS.ApplicationNotificationFetchDescriptorImpl>,
  ): Promise<Array<any>> {
    let request: CubiqTS.ApplicationNotificationsFetchRequestImpl =
      new CubiqTS.ApplicationNotificationsFetchRequestImpl();
    request.requestDescriptors = descriptors;

    return new Promise<Array<any>>((resolve, reject) => {
      Promise.all([
        this.orderPrivatizationService.isReady(),
        this.dealPrivatizationService.isReady(),
        this.worker.postServiceRequest(request),
      ]).then(
        ([, , payload]) => {
          let event: CubiqTS.StandardReportImpl = payload;
          let notifObjects: Array<any> = [];
          if (event && event.results) {
            event.results.forEach((result) => {
              let notifType: BFXNotificationType = undefined;
              let notifSubType: BFXNotificationSubType = undefined;
              if (result instanceof CubiqTS.OrderDataImpl) {
                if (result.orderState === CubiqTS.OrderState.CANCELLED) {
                  if (
                    result.orderType === CubiqTS.OrderType.BENCHMARK ||
                    result.orderType === CubiqTS.OrderType.BMALGO
                  ) {
                    notifType = BFXNotificationType.BMARK_CANCELLED;
                  } else {
                    notifType = BFXNotificationType.CANCELLED;
                  }
                } else if (result.orderState === CubiqTS.OrderState.EXPIRED) {
                  notifType = BFXNotificationType.EXPIRED;
                } else if (result.orderState === CubiqTS.OrderState.ACTIVE) {
                  if (
                    result.orderType === CubiqTS.OrderType.BENCHMARK ||
                    result.orderType === CubiqTS.OrderType.BMALGO
                  ) {
                    notifType = BFXNotificationType.BMARK_ORDER_ENTERED;
                  } else {
                    notifType = BFXNotificationType.LIMITALGO_ORDER_ENTERED;
                  }
                } else if (result.orderState === CubiqTS.OrderState.FILLED) {
                  // Full Fills show Order Details, even on login, Limits and BM's only, no BMAlgos
                  if (result.orderType === CubiqTS.OrderType.BENCHMARK) {
                    notifType = BFXNotificationType.BMARK_FILLED;
                    notifSubType = BFXNotificationSubType.FILL_FULL;
                  } else if (result.orderType !== CubiqTS.OrderType.BMALGO) {
                    notifType = BFXNotificationType.FILLED;
                    notifSubType = BFXNotificationSubType.FILL_FULL;
                  }
                }
                this.orderPrivatizationService.checkPrivatization(result);
                this.fireAppNotificationEvent(
                  this.orderDataToNotification(result, notifType, notifSubType),
                  BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
                );
              } else if (result instanceof CubiqTS.DealImpl) {
                this.dealPrivatizationService.checkPrivatization(result);

                if (
                  result.orderType === CubiqTS.OrderType.BENCHMARK ||
                  result.orderType === CubiqTS.OrderType.BMALGO
                ) {
                  notifType = BFXNotificationType.BMARK_FILLED;
                  notifSubType = BFXNotificationSubType.FILL_FULL;
                } else {
                  const partFilled = CubiqTS.FillState.PARTFILLED === result.fillState;

                  notifType = partFilled
                    ? BFXNotificationType.PART_FILLED
                    : BFXNotificationType.FILLED;
                  notifSubType = partFilled
                    ? BFXNotificationSubType.FILL_PART
                    : BFXNotificationSubType.FILL_FULL;
                }
                this.fireAppNotificationEvent(
                  this.tradeDataToNotification(result, notifType, notifSubType),
                  BFXAppNotificationEventType.DEAL_UPDATE_EVENT,
                );
              }
            });
          }
          resolve(payload?.results ?? []);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  /**
   *  Ensure deal rate is properly formatted
   *
   * @param value
   * @param baseCode
   * @param nonBaseCode
   * @param isInvert
   * @param productType
   * @param orderType
   * @returns
   */
  private formatRateValue(
    value: CubiqTS.BigNumber,
    baseCode: string,
    nonBaseCode: string,
    isInvert: boolean,
    productType: string,
    orderType: CubiqTS.OrderType,
  ): string {
    let currentDecs: number = value.decimalPlaces();
    if (baseCode && nonBaseCode) {
      const pair = this._ccyPairLookup.getCcyPair(baseCode, nonBaseCode);
      if (pair) {
        const minPrecision = this.getMinPrecision(
          pair,
          baseCode,
          nonBaseCode,
          isInvert,
          productType,
          orderType,
        );

        if (currentDecs < minPrecision) {
          return value.toFixed(minPrecision);
        }
      }
    }

    return value.toFixed(currentDecs);
  }

  private getMinPrecision(
    ccyPair: CubiqTS.CurrencyPairImpl,
    baseCode: string,
    nonBaseCode: string,
    isInvert: boolean,
    productType: string,
    orderType: CubiqTS.OrderType,
  ): number {
    let precision: number = 2;
    if (orderType === CubiqTS.OrderType.BENCHMARK || orderType === CubiqTS.OrderType.BMALGO) {
      if (!isInvert) {
        precision = ccyPair.bmSpotPrecision;
      } else if (isInvert) {
        precision = ccyPair.bmSpotInvPrecision;
      }
    } else {
      if (productType == "SPT" && !isInvert) {
        precision = ccyPair.rateDecimalsQty;
      } else if (productType == "SPT" && isInvert) {
        precision = ccyPair.spotInvertDecimalsQty;
      } else if (productType !== "SPT" && !isInvert) {
        precision = ccyPair.fwdRateDecimalsQty;
      } else if (productType !== "SPT" && isInvert) {
        precision = ccyPair.fwdInvertDecimalsQty;
      }
    }
    return precision;
  }

  /**
   * Get the popup notification type used to check for enabled
   * @param notifType
   * @param orderType
   * @returns
   */
  public getOrderPopupType(
    notifType: BFXNotificationType,
    orderType: CubiqTS.OrderType,
  ): BFXNotificationType {
    const isAlgo: Boolean = orderType === CubiqTS.OrderType.ALGO;
    const isBMAlgo: Boolean = orderType === CubiqTS.OrderType.BMALGO;

    let retType = notifType;
    if (isAlgo || isBMAlgo) {
      // We need to use the Algo-specific setting rather than the general setting
      switch (notifType) {
        case BFXNotificationType.ACTIVATED:
          retType = BFXNotificationType.ALGO_ACTIVATED;
          break;
        case BFXNotificationType.ACTIVATING:
          retType = BFXNotificationType.ALGO_ACTIVATING;
          break;
        case BFXNotificationType.CANCELLED:
          retType = BFXNotificationType.ALGO_CANCELLED;
          break;
        case BFXNotificationType.DEAL_FAILURE:
          retType = BFXNotificationType.ALGO_DEAL_FAILURE;
          break;
        case BFXNotificationType.LIMITALGO_ORDER_ENTERED:
          retType = BFXNotificationType.ALGO_LIMITALGO_ORDER_ENTERED;
          break;
        case BFXNotificationType.EXPIRED:
          retType = BFXNotificationType.ALGO_EXPIRED;
          break;
        case BFXNotificationType.EXPIRING:
          retType = BFXNotificationType.ALGO_EXPIRING;
          break;
        case BFXNotificationType.FILLED:
          retType = BFXNotificationType.ALGO_FILLED;
          break;
        case BFXNotificationType.PART_FILLED:
          retType = BFXNotificationType.ALGO_PART_FILLED;
          break;
        case BFXNotificationType.MODIFIED:
          retType = BFXNotificationType.ALGO_MODIFIED;
          break;
        case BFXNotificationType.ACCEPTANCE_NEEDED:
          retType = BFXNotificationType.ALGO_ACCEPTANCE_NEEDED;
          break;
        case BFXNotificationType.UNSOLICITED_CANCEL:
          retType = BFXNotificationType.ALGO_UNSOLICITED_CANCEL;
          break;
        case BFXNotificationType.UNSOLICITED_PAUSE_RESUME:
          retType = BFXNotificationType.ALGO_UNSOLICITED_PAUSE_RESUME;
          break;
      }
    }
    return retType;
  }

  /**
   * Get the application notification type used to check for enabled
   * @param notifType
   * @param orderType
   * @returns
   */
  public getOrderApplicationType(
    notifType: BFXAppNotificationType,
    orderType: CubiqTS.OrderType,
  ): BFXAppNotificationType {
    const isAlgo: Boolean = orderType === CubiqTS.OrderType.ALGO;
    const isBMAlgo: Boolean = orderType === CubiqTS.OrderType.BMALGO;

    let retType = notifType;
    if (isAlgo || isBMAlgo) {
      // We need to use the Algo-specific setting rather than the general setting
      switch (notifType) {
        case BFXAppNotificationType.ACTIVATED:
          retType = BFXAppNotificationType.ALGO_ACTIVATED;
          break;
        case BFXAppNotificationType.CANCELLED:
          retType = BFXAppNotificationType.ALGO_CANCELLED;
          break;
        case BFXAppNotificationType.EXPIRED:
          retType = BFXAppNotificationType.ALGO_EXPIRED;
          break;
        case BFXAppNotificationType.FILLED:
          retType = BFXAppNotificationType.ALGO_FILLED;
          break;
        case BFXAppNotificationType.MODIFIED:
          retType = BFXAppNotificationType.ALGO_MODIFIED;
          break;
        case BFXAppNotificationType.ENTERED:
          retType = BFXAppNotificationType.ALGO_ENTERED;
          break;
        case BFXAppNotificationType.PART_FILLED:
          retType = BFXAppNotificationType.ALGO_PART_FILLED;
          break;
      }
    }
    return retType;
  }
}
